/**
 * @file
 * @fileOverview This file contains the sound components for the Aframe scene.
 * @version 1.0.0
 * @author Parker Eli Bidigare
 * @date 2024-05-26
 */

AFRAME.registerComponent('three-d-sound-simple', {
    schema: {
      src: { type: 'string' },
      loop: { type: 'boolean', default: false },
      volume: { type: 'number', default: 1 },
      refDistance: { type: 'number', default: 1 },
      rolloffFactor: { type: 'number', default: 1 },
      autoplay: { type: 'boolean', default: false }
    },
  
    init: function () {
      this.listener = new THREE.AudioListener();
      this.sound = new THREE.PositionalAudio(this.listener);
      this.el.setObject3D('sound', this.sound);
  
      // Get the camera and add the listener to it
      const camera = document.querySelector('a-camera') || document.querySelector('[camera]');
      camera.object3D.add(this.listener);
  
      // Load the sound
      const audioLoader = new THREE.AudioLoader();
      audioLoader.load(this.data.src, (buffer) => {
        this.sound.setBuffer(buffer);
        this.sound.setRefDistance(this.data.refDistance);
        this.sound.setRolloffFactor(this.data.rolloffFactor);
        this.sound.setLoop(this.data.loop);
        this.sound.setVolume(this.data.volume);
  
        if (this.data.autoplay) {
          this.sound.play();
        }
      });
  
      // Add play and stop methods to the entity
      this.el.play = () => this.sound.play();
      this.el.stop = () => this.sound.stop();
    },
  
    update: function (oldData) {
      if (this.sound && this.sound.isPlaying) {
        this.sound.setVolume(this.data.volume);
        this.sound.setLoop(this.data.loop);
        this.sound.setRefDistance(this.data.refDistance);
        this.sound.setRolloffFactor(this.data.rolloffFactor);
      }
    },
  
    remove: function () {
      this.el.removeObject3D('sound');
    }
  });




  AFRAME.registerComponent('three-d-sound', {
    schema: {
      src: { type: 'string' },
      loop: { type: 'boolean', default: false },
      volume: { type: 'number', default: 1 },
      refDistance: { type: 'number', default: 1 },
      rolloffFactor: { type: 'number', default: 1 },
      autoplay: { type: 'boolean', default: false },
      roomEffect: { type: 'boolean', default: false },
      environment: { type: 'string', default: 'none', oneOf: ['none', 'underwater', 'muffled', 'echo'] }
    },
  
    init: function () {
      this.listener = new THREE.AudioListener();
      this.sound = new THREE.PositionalAudio(this.listener);
      this.el.setObject3D('sound', this.sound);
  
      // Get the camera and add the listener to it
      const camera = document.querySelector('a-camera') || document.querySelector('[camera]');
      camera.object3D.add(this.listener);
  
      // Create audio effects
      this.convolver = this.listener.context.createConvolver();
      this.biquadFilter = this.listener.context.createBiquadFilter();
  
      // Load the sound
      const audioLoader = new THREE.AudioLoader();
      audioLoader.load(this.data.src, (buffer) => {
        this.sound.setBuffer(buffer);
        this.sound.setRefDistance(this.data.refDistance);
        this.sound.setRolloffFactor(this.data.rolloffFactor);
        this.sound.setLoop(this.data.loop);
        this.sound.setVolume(this.data.volume);
  
        this.applyEffects();
  
        if (this.data.autoplay) {
          this.sound.play();
        }
      });
  
      // Load room impulse response
      audioLoader.load('https://threejs.org/examples/sounds/impulse-response/room.wav', (buffer) => {
        this.convolver.buffer = buffer;
      });
  
      // Add play and stop methods to the entity
      this.el.play = () => this.sound.play();
      this.el.stop = () => this.sound.stop();
    },
  
    update: function (oldData) {
      if (this.sound && this.sound.isPlaying) {
        this.sound.setVolume(this.data.volume);
        this.sound.setLoop(this.data.loop);
        this.sound.setRefDistance(this.data.refDistance);
        this.sound.setRolloffFactor(this.data.rolloffFactor);
      }
      this.applyEffects();
    },
  
    applyEffects: function () {
      if (!this.sound) return;
  
      // Disconnect existing effects
      this.sound.disconnect();
  
      // Apply room effect if enabled
      if (this.data.roomEffect) {
        this.sound.connect(this.convolver);
        this.convolver.connect(this.listener.context.destination);
      }
  
      // Apply environmental effect
      switch (this.data.environment) {
        case 'underwater':
          this.biquadFilter.type = 'lowpass';
          this.biquadFilter.frequency.setValueAtTime(300, this.listener.context.currentTime);
          this.sound.connect(this.biquadFilter);
          this.biquadFilter.connect(this.listener.context.destination);
          break;
        case 'muffled':
          this.biquadFilter.type = 'lowpass';
          this.biquadFilter.frequency.setValueAtTime(1000, this.listener.context.currentTime);
          this.sound.connect(this.biquadFilter);
          this.biquadFilter.connect(this.listener.context.destination);
          break;
        case 'echo':
          const delay = this.listener.context.createDelay(0.5);
          const feedback = this.listener.context.createGain();
          delay.delayTime.setValueAtTime(0.3, this.listener.context.currentTime);
          feedback.gain.setValueAtTime(0.5, this.listener.context.currentTime);
          this.sound.connect(delay);
          delay.connect(feedback);
          feedback.connect(delay);
          delay.connect(this.listener.context.destination);
          this.sound.connect(this.listener.context.destination);
          break;
        default:
          // No effect, connect directly to the destination
          this.sound.connect(this.listener.context.destination);
      }
    },
  
    remove: function () {
      this.el.removeObject3D('sound');
    }
  });

  export const SoundComponent = {
    threedsound: AFRAME.components['three-d-sound'],
    threedsoundSimple: AFRAME.components['three-d-sound-simple']
  };